import { CompanyOneSsqOverview, CompanyOverviewContext, CompanyOverviewInit } from "./context";
import { useEntityScores } from "../../Scores/useEntityScores";
import { CompanyOverview, useOrganizationOverview } from "src/api/client-api/company/overview/useOrganizationOverview";
import { useCompanyMetadata } from "../Metadata/useCompanyMetadata";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { Loading } from "src/components/utils/Loading";
import { useGetSsqVersionAssignment } from "src/api/client-api/questionnaire/useGetSsqVersionAssignment.ts";

type CompanyProviderProps = {
    children: ReactNode;
    ssqId: string | undefined;
};
export const CompanyOverviewProvider = (props: CompanyProviderProps) => {
    const { children, ssqId } = props;
    const { selectedVariations, selectVariation } = useEntityScores();
    const { company } = useCompanyMetadata();
    const { currentCampaign } = useFoodpilotContext();

    const { overview, isReady, isRefetching } = useOrganizationOverview({
        campaignId: currentCampaign?.id,
        companyId: company.id,
        variations: selectedVariations.query,
    });

    const { ssqVersionAssignment, isReady: isAssignmentReady } = useGetSsqVersionAssignment(
        ssqId ? parseInt(ssqId) : undefined,
        currentCampaign?.id,
    );

    const ssqVersionId = ssqVersionAssignment?.ssqVersionId;

    if (!isReady || !isAssignmentReady) {
        return <Loading />;
    }

    if (overview === undefined) {
        // Error state
        console.error("Overview does not exist, it is not possible. Unhandled error.");
        return null;
    }

    const filteredOverview = getFilteredOverview({ overview, ssqId });
    const items: CompanyOverviewInit = {
        companyId: company.id,
        ssqId,
        ssqVersionId,
        isRefetching,
        filteredOverview,
        overview,
        selectVariation,
    };

    return <CompanyOverviewContext.Provider value={items}>{children}</CompanyOverviewContext.Provider>;
};

const getFilteredOverview = (options: {
    overview: CompanyOverview;
    ssqId: string | undefined;
}): CompanyOneSsqOverview | null => {
    const { overview, ssqId } = options;
    if (ssqId === undefined) {
        return null;
    }
    const ssqIdNumber = Number(ssqId);
    if (isNaN(ssqIdNumber)) {
        return null;
    }

    const filteredSsq = overview.ssqs.find((v) => {
        return v.id === ssqIdNumber;
    });
    if (filteredSsq === undefined) {
        return null;
    }

    const _scoreListToKeep = filteredSsq.scores.map((score) => score.id);
    const scoreListToKeep = new Set(_scoreListToKeep);
    const filteredScores = overview.scores.filter((v) => {
        return scoreListToKeep.has(v.id);
    });

    const filteredActionPlan =
        overview.actionPlans.find((actPlan) => {
            return actPlan.ssqId === ssqIdNumber;
        }) ?? null;

    const filteredOverview: CompanyOneSsqOverview = {
        ssq: filteredSsq,
        scores: filteredScores,
        actionPlan: filteredActionPlan,
    };
    return filteredOverview;
};
